import { type TruckType } from 'core/domain/Truck'
import { createDate } from 'core/utils/date'

import { type Driver } from '../../domain/Driver'
import { type LastReportedLocation } from '../../domain/shipment/LastReportedLocation'
import { type Shipment } from '../../domain/shipment/Shipment'
import { type ShipmentAppointment } from '../../domain/shipment/ShipmentAppointment'
import { type ShipmentContact } from '../../domain/shipment/ShipmentContact'
import { type ShipmentProof } from '../../domain/shipment/ShipmentProof'
import { type ShipmentStatus } from '../../domain/shipment/ShipmentStatus'
import { type ShipmentStop } from '../../domain/shipment/ShipmentStop'
import { mapApiToDriver, type RawDriver } from '../driverMapper'

type RawLastReportedLocation = {
  location: string
  timestamp: string
}

type RawShipmentAppointment = {
  confirmed_at: string | null
}

type RawShipmentContact = {
  blind_bol: boolean | null
  company_name: string
  email: string | null
  first_name: string | null
  instructions: string
  last_name: string | null
  phone_number_extension: string | null
  phone_number: string | null
  scale_ticket: boolean | null
}

type RawShipmentProof = {
  id: string
  url: string
}

type RawShipmentStop = {
  accessorials: Array<String>
  address: string
  appointment: RawShipmentAppointment | null
  checked_in_at: string | null
  checked_out_at: string | null
  city: string
  contact: RawShipmentContact
  country: string
  free_detention_minutes: number
  id: string
  index: number
  is_eligible_for_detention: boolean
  latitude: string
  longitude: string
  need_appointment: boolean
  proofs: Array<RawShipmentProof>
  state: string
  stop_customer_ref: string | null
  timezone: string
  type: number
  window_end: string
  window_start: string
  zipcode: string
}

export type RawShipment = {
  id: string
  bol_number: string | null
  commodity: string
  created_at: string
  driver: RawDriver | null
  equipment_type: TruckType
  last_reported_location: RawLastReportedLocation | null
  mileage: number
  ref_number: string
  status: ShipmentStatus
  stops: Array<RawShipmentStop>
  temperature: number | null
  tractor_number: string | null
  trailer_number: string | null
  truck: string | null
  uuid: string
  weight: number
}

function mapLastReportedLocation(
  reportedLocation: RawLastReportedLocation | null
): LastReportedLocation | undefined {
  if (!reportedLocation) return

  return {
    location: reportedLocation.location,
    timestamp: reportedLocation.timestamp,
  }
}

function mapShipmentAppointment(
  shipmentAppointment: RawShipmentAppointment | null
): ShipmentAppointment | undefined {
  if (!shipmentAppointment) return

  return {
    confirmedAt: createDate(shipmentAppointment.confirmed_at),
  }
}

function mapShipmentContact(contact: RawShipmentContact | null): ShipmentContact | undefined {
  if (!contact) return

  return {
    blindBol: Boolean(contact.blind_bol),
    companyName: contact.company_name,
    email: contact.email ?? undefined,
    firstName: contact.first_name ?? undefined,
    instructions: contact.instructions ?? undefined,
    lastName: contact.last_name ?? undefined,
    phoneNumberExtension: contact.phone_number_extension ?? undefined,
    phoneNumber: contact.phone_number ?? undefined,
    scaleTicket: Boolean(contact.scale_ticket),
  }
}

function mapShipmentProof(proof: RawShipmentProof): ShipmentProof {
  return {
    id: proof.id,
    url: proof.url,
  }
}

function mapShipmentStop(stop: RawShipmentStop): ShipmentStop {
  const appointment: ShipmentAppointment | undefined = mapShipmentAppointment(stop.appointment)
  const contact: ShipmentContact | undefined = mapShipmentContact(stop.contact)
  const proofs: Array<ShipmentProof> = stop.proofs.map(proof => mapShipmentProof(proof))

  return {
    accessorials: stop.accessorials,
    address: stop.address,
    appointment,
    checkedInAt: createDate(stop.checked_in_at),
    checkedOutAt: createDate(stop.checked_out_at),
    city: stop.city,
    contact,
    country: stop.country,
    freeDetentionMinutes: stop.free_detention_minutes,
    id: stop.id,
    index: stop.index,
    isEligibleForDetention: Boolean(stop.is_eligible_for_detention),
    latitude: stop.latitude,
    longitude: stop.longitude,
    needAppointment: Boolean(stop.need_appointment),
    proofs,
    state: stop.state,
    stopCustomerRef: stop.stop_customer_ref ?? undefined,
    timezone: stop.timezone,
    type: stop.type,
    windowEnd: new Date(stop.window_end),
    windowStart: new Date(stop.window_start),
    zipcode: stop.zipcode,
  }
}

export function mapApiToShipment(rawShipment: RawShipment): Shipment {
  let driver: Driver | undefined
  if (rawShipment.driver) {
    driver = mapApiToDriver(rawShipment.driver)
  }
  const lastReportedLocation: LastReportedLocation | undefined = mapLastReportedLocation(
    rawShipment.last_reported_location
  )
  const stops = (rawShipment.stops ?? []).map(mapShipmentStop)
  const shipment: Shipment = {
    id: rawShipment.id,
    bolNumber: rawShipment.bol_number ?? undefined,
    commodity: rawShipment.commodity,
    createdAt: new Date(rawShipment.created_at),
    driver,
    equipmentType: rawShipment.equipment_type,
    lastReportedLocation,
    mileage: rawShipment.mileage,
    refNumber: rawShipment.ref_number,
    status: rawShipment.status,
    stops,
    temperature: rawShipment.temperature ?? undefined,
    tractorNumber: rawShipment.tractor_number ?? undefined,
    trailerNumber: rawShipment.trailer_number ?? undefined,
    truck: rawShipment.truck ?? undefined,
    uuid: rawShipment.uuid,
    weight: rawShipment.weight,
  }

  return shipment
}
